import React, { useEffect, useState, useReducer, useCallback, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Box, Button, FlexBox, Pagination, SiteSearchResult } from 'dma-ui-kit';
import styled from 'styled-components';
import { translateInterfaceText } from 'helpers/translation';
import { SiteSearchableSection } from 'search/SiteSearchableSection';
import { reducer as queryReducer } from 'search/queries';
import resultMissing from 'assets/resultmissing.png';
import ImageNotFound from 'assets/missing.png';
import { buildIIIFImageURL } from 'helpers/collection';
import { Body, red, grey400, grey600, breakpoints } from 'dma-ui-kit';
import useBreakpoint from 'use-breakpoint';

const Container = styled(Box)`
  padding: 0 12px;
  @media (min-width: 1025px) {
      padding: 0 48px;
  }
  @media (max-width: 1024px) {
      padding: 0 24px;
  }    

  margin-bottom: 10px;
  display: block;
`;

const StickySearch = styled(FlexBox)`
  background-color: white;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: sticky;
  margin-bottom: 24px;
  top: -1px;
  width: 100vw;
  /* sticky creates a stacking context; lift it above page content (footer etc.)
     so the suggestions dropdown isn't painted underneath, but keep it below the
     site menus (z-index 999/1000 in dma-ui-kit). */
  z-index: 10;
`;

const PaginationContainer = styled(Box)`
  max-width: 320px;
  margin: auto;

  @media (min-width: 1025px) {
      max-width: 800px;
  }
  @media (max-width: 1024px) {
      max-width: 640px;
  }    
`;

const FilterContainer = styled(FlexBox)`
  justify-content: center;
  margin: 20px 0;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? red : 'transparent'};
  color: ${props => props.active ? 'white' : red };
  border: 1px solid ${red};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? red : grey600};
  }
`;

const InputContainer = styled(Box)`
  display: flex;
  padding: 10px 0;
  align-items: baseline;
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${grey400};
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 2002;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${grey400};
  transition: background-color 0.2s ease;
  background-color: ${props => props.$active ? grey600 : 'transparent'};
  
  &:hover {
    background-color: ${grey600};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionText = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  color: ${red};
  margin-bottom: 4px;
`;

const Input = styled.input`
  font-family: 'usual';
  font-weight: 400;
  font-size: 2rem;
  width: 100%;
  font-style: italic;
  margin-right: 20px;
  box-sizing: border-box;
  padding: 10px;
  color: ${red};
  background-color: transparent;
  border: none;
  border-bottom: 4px solid ${red};

  &:focus {
    outline: none;
    border-bottom: 4px solid ${red};
  }

  ::placeholder {
    font-style: none;
    color: ${grey400};
  }
`;


const SiteSearch = React.memo(() => {

  const { breakpoint } = useBreakpoint(breakpoints);

  const searchURL = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search`;
  const suggestURL = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/suggest`;

  const history = useHistory();

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const initialTerm = params.get('s');
  const initialPage = params.get('p');
  const initialFilter = params.get('f');

  const [query, setQuery] = useState('');
  const [nextTerm, setNextTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debounceTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  const [options, dispatch] = useReducer(
    queryReducer,
    {  
      page: initialPage || 1,
      term: initialTerm || '',
      filter: initialFilter || '',
      limit: 20
    },  
  );

  useEffect(() => {
    const nextQuery = options;
    setQuery(nextQuery);
  }, [setQuery, options]);

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${suggestURL}?q=${encodeURIComponent(term)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.suggestions && Array.isArray(data.suggestions)) {
          setSuggestions(data.suggestions);
          setActiveIndex(-1);
          setShowSuggestions(true);
        }
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    }
  }, [suggestURL]);

  // Debounced suggestion fetching
  const debouncedFetchSuggestions = useCallback((term) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(term);
    }, 300);
  }, [fetchSuggestions]);

  // Handle clicks outside suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);


  const searchWidth = () => {
    switch(breakpoint) {
        case "mobile":
            return "80%";
        case "tablet":
        case "tabletSmall":
            return "70%";
        default:
            return "60%";
    };
  };

  const handleTerm = e => {
    const value = e.target.value;
    setNextTerm(value);
    setActiveIndex(-1);
    debouncedFetchSuggestions(value);
  };

  const handleKeyDown = (e) => {
    const open = showSuggestions && suggestions.length > 0;

    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIndex >= 0 && activeIndex < suggestions.length) {
        handleSuggestionClick(suggestions[activeIndex]);
      } else {
        handleTermChange();
      }
      setShowSuggestions(false);
      setActiveIndex(-1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setNextTerm(suggestion);
    setShowSuggestions(false);
    
    // Immediately trigger search with the selected suggestion
    dispatch({ type: 'setTerm', payload: suggestion });
    dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
    dispatch({ type: 'setFilter', payload: '' });
    
    history.push(`/search?s=${suggestion}&p=1`);
    window.scrollTo(0, 0);
  };

  const handleTermChange = () => {
    // Because we display the current term as a placeholder, let's
    // pretend the user means to run a fresh search for the same term
    // if they submit an empty search
    const next = nextTerm ? nextTerm : options.term;

    dispatch({ type: 'setTerm', payload: next });
    dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});

    // Evaluate against nextTerm here to clear filters on a fresh search
    if (nextTerm !== options.term) {
      dispatch({ type: 'setFilter', payload: '' });
    }
    const filterParam = (nextTerm === options.term && options.filter) ? `&f=${options.filter}` : '';

    history.push(`/search?s=${next}&p=1${filterParam}`);
    window.scrollTo(0, 0);
  };

  const handlePageChange = val => {
    dispatch({ type: 'setPagination', payload: { page: val, limit: options.limit }});
    const filterParam = options.filter ? `&f=${options.filter}` : '';
    history.push(`/search?s=${options.term}&p=${val}${filterParam}`);
    window.scrollTo(0, 0);
  };

  const handleFilterChange = filter => {
    dispatch({ type: 'setFilter', payload: filter });
    dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
    const filterParam = filter ? `&f=${filter}` : '';
    history.push(`/search?s=${options.term}&p=1${filterParam}`);
    window.scrollTo(0, 0);
  };

  const labels = {
    audio_recording: translateInterfaceText('Audio Recording'),
    enterSearchTerm: translateInterfaceText('Enter search term'),
    loading: translateInterfaceText('Loading...'),
    object: translateInterfaceText('Artwork'),
    past_exhibition: translateInterfaceText('Past Exhibition'),
    readMore: translateInterfaceText('Read more'),
    resultsFound: translateInterfaceText('results found for'),
    search: translateInterfaceText('Search'),
    viewObject: translateInterfaceText('View object')
  };

  const resultType = ( type ) => {
    let typeLabel = labels[type] ? labels[type] : type;
    return typeLabel;
  };

  const AggregationFilters = ({ aggregations }) => {
    if (!aggregations || aggregations.length === 0) return null;

    // Calculate total count
    const totalCount = aggregations.reduce((sum, agg) => sum + agg.count, 0);
    
    // Function to format filter labels
    const formatFilterLabel = (key) => {
      const labelMap = {
        'artwork': 'Artwork',
        'past_exhibition': 'Past Exhibition',
        'audio_recording': 'Audio Recording'
      };
      return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };
    
    return (
      <FilterContainer>
        <FilterButton
          active={!options.filter}
          onClick={() => handleFilterChange('')}
        >
          All ({totalCount})
        </FilterButton>
        {aggregations.map(agg => (
          <FilterButton
            key={agg.key}
            active={options.filter === agg.key}
            onClick={() => handleFilterChange(agg.key)}
          >
            {formatFilterLabel(agg.key)} ({agg.count})
          </FilterButton>
        ))}
      </FilterContainer>
    );
  };

  return (
    <Container>
      <SiteSearchableSection query={query} endpoint={searchURL} historyPath={"/search"}>
        {({ results, loading }) => {
          
          return (
            <>
              <StickySearch>
                <Box w={searchWidth()}>
                  <InputContainer ref={suggestionsRef}>
                    <Input
                      onChange={handleTerm}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (suggestions.length > 0 && nextTerm.length >= 2) {
                          setShowSuggestions(true);
                        }
                      }}
                      type={'text'}
                      value={nextTerm}
                      placeholder={options?.term ? options.term : labels.enterSearchTerm}
                    />
                    <Box>
                      <Button
                        variant="outline"
                        label={labels.search}
                        onClick={handleTermChange}
                        compact={true}
                        color={red}
                      />
                    </Box>
                    {showSuggestions && suggestions.length > 0 && (
                      <SuggestionsDropdown>
                        {suggestions.map((suggestion, index) => (
                          <SuggestionItem
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            onMouseEnter={() => setActiveIndex(index)}
                            $active={index === activeIndex}
                          >
                            <SuggestionText>{suggestion}</SuggestionText>
                          </SuggestionItem>
                        ))}
                      </SuggestionsDropdown>
                    )}
                  </InputContainer>
                </Box>
                {options.term && results?.aggregations && 
                  <AggregationFilters aggregations={results.aggregations} />
                }
              </StickySearch>
              {options.term && results?.hits?.length > 0 && 
                <>
                  <div style={{ display: "block", width: '80%', margin: 'auto' }}>
                    <div>
                      {results.hits.map((result, index) => {

                        if (result.type && result.type == "content")
                          return (
                              <SiteSearchResult
                                key={`search-result-${index}`}
                                type={resultType(result.source.type[0])}
                                title={result.title[0]}
                                body={result.processed && result.processed[0] ? result.processed[0] : ''}
                                action={() => history.push(result.url[0])}
                                actionLabel={labels.readMore}
                                image={result.image && result.image[0] ? result.image[0] : resultMissing}
                                style={{ marginBottom: '20px' }}
                              />
                          );

                        if (result.type && result.type == "collection")
                        {

                          const bodyContent = (
                            <>
                              <Body bold>{result.artist ? result.artist : result.culture ? result.culture : ''}{result.artist || result.culture ? ', ' : ''}{result.dated ? result.dated : ''}</Body>
                              {result.medium && (<Body italic>{result.medium}</Body>)}
                              {result.source && result.source.credit_line && (<Body variant="tiny">{result.source.credit_line}</Body>)}
                            </>
                          );

                          return (
                              <SiteSearchResult
                                key={`search-result-${index}`}
                                type={resultType('object')}
                                title={`${result.number} ${result.title}`}
                                body={bodyContent}
                                action={() => history.push('/art/collection/object/' + result.id)}
                                actionLabel={labels.readMore}
                                image={(result.id && result.primary_image && result.primary_image != -1) ? buildIIIFImageURL(result.id, result.primary_image, { size: 320, type: 'square' }) : ImageNotFound}
                                mb={'30px'}
                              />
                          );
                        } 
                      })}

                    </div>
                  </div>
                  <PaginationContainer>
                    <Pagination
                      currentPage={options.page}
                      pageCount={results.pageCount}
                      onChange={handlePageChange}
                    />
                  </PaginationContainer>            
                </>
              }
            </>
          );
        }}
      </SiteSearchableSection>
    </Container>
  );
});

export default SiteSearch;
